import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

/**
 * ConsultingCatalog
 *
 * Props:
 *  - platformUrl (string): Blackboard Learn base URL (required for role check)
 *  - backEndUrl? (string): if provided, will try:
 *      - GET/PUT/PATCH/POST/DELETE `${backEndUrl}/consulting/tools`
 *  - initialItems? (Array<Tool>): optionally override the built-in sample catalog
 *
 * URL Params:
 *  - uuid: user uuid (required for admin check)
 *  - token: bearer token (used for Learn and backend calls)
 *  - q: search text
 *  - sort: column to sort by; prefix with '-' for descending (e.g., "-priceUsd")
 *
 * Tool shape:
 *  {
 *    id: string,                         // SK
 *    name: string,
 *    description: string,
 *    priceUsd: number,
 *    availability: "Now" | "Scheduled" | "Waitlist",
 *    availableFrom?: string,             // ISO date, optional
 *    availableTo?: string,               // ISO date, optional
 *    customersCount: number,
 *    category: string,                   // PK
 *    tags?: string[],
 *    videoUrl?: string                   // optional demo video link
 *  }
 */

const defaultCatalog = [
    {
        id: "lti-deep-linking-suite",
        name: "LTI Deep Linking Suite",
        description:
            "End-to-end setup for LTI 1.3 deep links, gradable items, and grade passback orchestration.",
        priceUsd: 9000,
        availability: "Now",
        customersCount: 18,
        category: "Integrations",
        tags: ["LTI 1.3", "Gradebook", "IMS"],
        videoUrl: "https://www.youtube.com/watch?v=5qap5aO4i9A"
    },
    {
        id: "grade-hurdle-admin",
        name: "Grade Hurdle Admin",
        description:
            "Rules engine for hurdle/threshold requirements with override workflows and audit logs.",
        priceUsd: 6500,
        availability: "Scheduled",
        availableFrom: "2025-11-01",
        customersCount: 7,
        category: "LMS",
        tags: ["Blackboard", "Policies", "Compliance"]
    },
    {
        id: "late-penalty-rules",
        name: "Late Penalty Rules",
        description:
            "Configurable late penalty schemas, grace windows, and automatic score adjustments.",
        priceUsd: 5500,
        availability: "Now",
        customersCount: 22,
        category: "LMS",
        tags: ["Grades", "Automation"]
    },
    {
        id: "course-crawler-ai",
        name: "Course Crawler + AI Q&A",
        description:
            "Ingests course content, builds embeddings, and enables secure AI Q&A for student support.",
        priceUsd: 12000,
        availability: "Waitlist",
        customersCount: 5,
        category: "AI",
        tags: ["Embeddings", "Search", "Chat"]
    },
    {
        id: "reach-data-flows",
        name: "Reach Data Flows & CRM Sync",
        description:
            "Bi-directional sync of enrollment, progress, and completions to CRM (Salesforce/D365).",
        priceUsd: 14000,
        availability: "Scheduled",
        availableFrom: "2025-12-01",
        customersCount: 4,
        category: "Integrations",
        tags: ["CRM", "Salesforce", "D365"]
    },
    {
        id: "rubrics-export-publish",
        name: "Rubrics Export & Publish",
        description:
            "Export, version, and publish rubrics at scale with JSON/CSV workflows and bulk actions.",
        priceUsd: 4800,
        availability: "Now",
        customersCount: 11,
        category: "LMS",
        tags: ["Rubrics", "Bulk Ops"],
        videoUrl: "https://youtu.be/dQw4w9WgXcQ"
    },
    // Requested samples
    {
        id: "deploy-external-assignments",
        name: "Deploy External Assignments Tool",
        description:
            "Create and deploy external/LTI assignments in bulk with optional gradable settings and deep-link automation.",
        priceUsd: 7000,
        availability: "Now",
        customersCount: 16,
        category: "Integrations",
        tags: ["LTI 1.3", "Deep Linking", "Gradebook"]
    },
    {
        id: "video-studio-migration",
        name: "Video Studio Migration Tool",
        description:
            "Migrate media and embedded links at scale, remap URLs, and verify course references post-migration.",
        priceUsd: 10000,
        availability: "Scheduled",
        availableFrom: "2025-11-10",
        customersCount: 6,
        category: "Content",
        tags: ["Migration", "Media", "Validation"]
    },
    {
        id: "rubrics-exchange",
        name: "Rubrics Exchange Tool",
        description:
            "Import/export rubrics between courses and institutions, reconcile criteria, and publish safely.",
        priceUsd: 5200,
        availability: "Now",
        customersCount: 9,
        category: "LMS",
        tags: ["Rubrics", "Import/Export"]
    },
    {
        id: "assessment-student-profile",
        name: "Assessment Student Profile Tool",
        description:
            "One-pane student snapshot for assessments: attempts, submissions, accommodations, and risk indicators.",
        priceUsd: 6000,
        availability: "Now",
        customersCount: 13,
        category: "Analytics",
        tags: ["Assessment", "Student Profile", "Insights"]
    },
    {
        id: "late-penalty-tool",
        name: "Late Penalty Tool",
        description:
            "Flexible schemas to apply late penalties automatically with exceptions and audit history.",
        priceUsd: 5500,
        availability: "Now",
        customersCount: 22,
        category: "LMS",
        tags: ["Grades", "Policies", "Automation"]
    },
    {
        id: "extra-credit-tool",
        name: "Extra Credit Tool",
        description:
            "Define extra-credit rules and apply them consistently across sections with reporting.",
        priceUsd: 4300,
        availability: "Now",
        customersCount: 14,
        category: "LMS",
        tags: ["Grades", "Rules"]
    },
    {
        id: "grade-hurdle-tool",
        name: "Grade Hurdle Tool",
        description:
            "Configure grade thresholds for progression and integrate with release conditions.",
        priceUsd: 6500,
        availability: "Scheduled",
        availableFrom: "2025-11-01",
        customersCount: 7,
        category: "LMS",
        tags: ["Hurdles", "Compliance"]
    },
    {
        id: "scheduled-grades-posting",
        name: "Scheduled Grades Posting Tool",
        description:
            "Schedule automatic grade posting to align with policy windows and instructor approvals.",
        priceUsd: 5800,
        availability: "Now",
        customersCount: 10,
        category: "Automation",
        tags: ["Scheduler", "Gradebook", "Compliance"]
    },
    {
        id: "doc-course-links",
        name: "Adding Course Links to Documents Tool",
        description:
            "Now Instructors can add links to course materials inside the documents.  They can give students convenient links to their readings, assignments, tests, discussions, etc.",
        priceUsd: 4900,
        availability: "Now",
        customersCount: 8,
        category: "Content",
        tags: ["Links", "Documents", "Validation"]
    },
    {
        id: "ai-course-chatbot",
        name: "AI Course Chatbot Tool",
        description:
            "Context-aware course assistant powered by embeddings and guardrails for accurate answers.",
        priceUsd: 12000,
        availability: "Waitlist",
        customersCount: 5,
        category: "AI",
        tags: ["Chatbot", "Embeddings", "Guardrails"]
    },
    {
        id: "certificates-tool",
        name: "Certificates Tool",
        description:
            "Issue completion certificates with templating, verification QR codes, and LMS/CRM sync.",
        priceUsd: 6200,
        availability: "Now",
        customersCount: 12,
        category: "Credentials",
        tags: ["Certificates", "Badging", "Sync"]
    }
];

function useQuery() {
    const location = useLocation();
    return new URLSearchParams(location.search);
}

const currency = (n) =>
    typeof n === "number"
        ? n.toLocaleString("en-US", { style: "currency", currency: "USD" })
        : "—";

const dateShort = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
};

const isHttpUrl = (s) => {
    if (!s) return false;
    try {
        const u = new URL(String(s));
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
};

const extractYouTubeId = (url) => {
    try {
        const u = new URL(url);
        if (/youtube\.com$/i.test(u.hostname)) {
            const v = u.searchParams.get("v");
            if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) return v;
            // youtu.be/embed formats inside youtube.com
            const m = u.pathname.match(/\/embed\/([A-Za-z0-9_-]{11})/);
            if (m) return m[1];
        }
        if (/youtu\.be$/i.test(u.hostname)) {
            const m = u.pathname.match(/^\/([A-Za-z0-9_-]{11})/);
            if (m) return m[1];
        }
    } catch {}
    return "";
};

const headers = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    { key: "priceUsd", label: "Price" },
    { key: "availability", label: "Availability" },
    { key: "customersCount", label: "Customers" },
    { key: "category", label: "Category" },
    { key: "tags", label: "Tags" },
    { key: "videoUrl", label: "Demo" },
    { key: "__actions", label: "" } // admin buttons
];

const availabilityOptions = ["Now", "Scheduled", "Waitlist"];

const ConsultingCatalog = ({ platformUrl, backEndUrl, initialItems }) => {
    const query = useQuery();
    const urlUuid = query.get("uuid") || "";
    const token = query.get("token") || "";
    const initialSearch = query.get("q") || "";
    const urlSort = query.get("sort") || "name";

    const [items, setItems] = useState(initialItems || defaultCatalog);
    const [loading, setLoading] = useState(!!backEndUrl);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");

    const [search, setSearch] = useState(initialSearch);
    const [category, setCategory] = useState("All");
    const [availability, setAvailability] = useState("All");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");

    const [sortKey, setSortKey] = useState(urlSort.replace(/^-/, ""));
    const [sortDir, setSortDir] = useState(urlSort.startsWith("-") ? "desc" : "asc");

    const [isAdmin, setIsAdmin] = useState(false);

    const fileInputRef = useRef(null);

    // Edit modal state
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);

    // Add modal state
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [newItem, setNewItem] = useState({
        id: "",
        category: "",
        name: "",
        description: "",
        priceUsd: "",
        availability: "Now",
        availableFrom: "",
        availableTo: "",
        customersCount: "",
        tags: "",
        videoUrl: ""
    });

    const authHeaders = useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    // 1) Fetch user info from Learn to determine ADMIN institution role
    useEffect(() => {
        let cancelled = false;

        const fetchUser = async () => {
            if (!platformUrl || !urlUuid || !token) {
                if (!cancelled) setIsAdmin(false);
                return;
            }
            try {
                const res = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/uuid:${urlUuid}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const u = res?.data || {};
                const roles = u?.institutionRoleIds || [];
                const admin = Array.isArray(roles) && roles.includes("SC_ADMIN");
                if (!cancelled) setIsAdmin(!!admin);
            } catch (err) {
                console.error("Failed to fetch user info for role check:", err);
                if (!cancelled) setIsAdmin(false);
            }
        };

        fetchUser();
        return () => {
            cancelled = true;
        };
    }, [platformUrl, urlUuid, token]);

    // 2) Fetch catalog from backend if provided
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!backEndUrl) return;
            try {
                setLoading(true);
                setError("");
                setInfo("");
                const res = await axios.get(`${backEndUrl}/consulting/tools`, {
                    headers: authHeaders
                });

                // Backend returns { items, count, nextKey }.
                let data = [];
                if (Array.isArray(res.data?.items)) data = res.data.items;
                else if (Array.isArray(res.data)) data = res.data; // fallback if lambda ever returns raw
                if (!cancelled) {
                    if (data.length) {
                        setItems(data);
                    } else {
                        // Show samples, allow seeding
                        setItems(defaultCatalog);
                        setInfo(
                            "No items found in the backend. Showing sample catalog — you can seed these to the backend."
                        );
                    }
                }
            } catch (err) {
                console.error("Error loading catalog:", err);
                if (!cancelled) {
                    setError(
                        err.response?.data?.message ||
                        "Failed to load catalog. Showing sample items."
                    );
                    setItems(defaultCatalog);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [backEndUrl, authHeaders]);

    // 3) Filters
    const categories = useMemo(() => {
        const set = new Set(items.map((i) => i.category).filter(Boolean));
        return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
    }, [items]);

    const filtered = useMemo(() => {
        const s = (search || "").trim().toLowerCase();

        const inRange = (price) => {
            const min = minPrice === "" ? -Infinity : Number(minPrice);
            const max = maxPrice === "" ? Infinity : Number(maxPrice);
            if (Number.isNaN(min) || Number.isNaN(max)) return true;
            return price >= min && price <= max;
        };

        return items.filter((it) => {
            const textHit =
                !s ||
                it.name?.toLowerCase().includes(s) ||
                it.description?.toLowerCase().includes(s) ||
                it.category?.toLowerCase().includes(s) ||
                (it.tags || []).some((t) => t.toLowerCase().includes(s)) ||
                (it.videoUrl || "").toLowerCase().includes(s);

            const catHit = category === "All" || it.category === category;
            const availHit = availability === "All" || it.availability === availability;
            const priceHit = inRange(Number(it.priceUsd ?? 0));

            return textHit && catHit && availHit && priceHit;
        });
    }, [items, search, category, availability, minPrice, maxPrice]);

    // 4) Sorting
    const sorted = useMemo(() => {
        const dir = sortDir === "desc" ? -1 : 1;
        const k = sortKey;
        return [...filtered].sort((a, b) => {
            const va = a?.[k];
            const vb = b?.[k];

            if (k === "priceUsd" || k === "customersCount") {
                const na = Number(va ?? 0);
                const nb = Number(vb ?? 0);
                return (na - nb) * dir;
            }

            if (k === "tags") {
                const la = (a.tags || []).length;
                const lb = (b.tags || []).length;
                return (la - lb) * dir;
            }

            const sa = (va ?? "").toString().toLowerCase();
            const sb = (vb ?? "").toString().toLowerCase();
            return sa.localeCompare(sb) * dir;
        });
    }, [filtered, sortKey, sortDir]);

    const onSort = (key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const clearFilters = () => {
        setSearch("");
        setCategory("All");
        setAvailability("All");
        setMinPrice("");
        setMaxPrice("");
    };

    // 5) Admin-only actions: Export / Import / Save All / Edit / Delete / Add New

    const csvEscape = (val) => {
        const s = String(val ?? "");
        if (/[",\n]/.test(s)) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };

    const exportCsv = () => {
        const cols = [
            "id",
            "name",
            "description",
            "priceUsd",
            "availability",
            "availableFrom",
            "availableTo",
            "customersCount",
            "category",
            "tags",
            "videoUrl"
        ];

        const rows = sorted.map((it) => {
            const obj = {
                ...it,
                tags: (it.tags || []).join(";")
            };
            return cols.map((c) => csvEscape(obj[c])).join(",");
        });

        const csv = [cols.join(","), ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "consulting_catalog.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const triggerImport = () => fileInputRef.current?.click();

    // Falls back to PUT-per-item if a chunk POST fails.
    const saveArrayToBackend = async (arr) => {
        if (!backEndUrl || !arr?.length) return;
        setInfo("");
        setError("");

        const CHUNK = 25;
        const chunks = [];
        for (let i = 0; i < arr.length; i += CHUNK) chunks.push(arr.slice(i, i + CHUNK));

        let written = 0;
        try {
            for (const c of chunks) {
                try {
                    await axios.post(`${backEndUrl}/consulting/tools`, c, {
                        headers: { ...authHeaders, "Content-Type": "application/json" }
                    });
                    written += c.length;
                } catch (postErr) {
                    // Fallback: PUT each item from this chunk
                    console.warn(
                        "Chunk POST failed, falling back to PUT-per-item:",
                        postErr?.response?.status,
                        postErr?.message
                    );
                    for (const item of c) {
                        try {
                            await axios.put(`${backEndUrl}/consulting/tools`, item, {
                                headers: { ...authHeaders, "Content-Type": "application/json" }
                            });
                            written += 1;
                        } catch (putErr) {
                            // If PUT fails because it exists, try PATCH
                            if (putErr?.response?.status === 409) {
                                await axios.patch(`${backEndUrl}/consulting/tools`, item, {
                                    headers: { ...authHeaders, "Content-Type": "application/json" }
                                });
                                written += 1;
                            } else {
                                console.error("PUT/PATCH failed for item", item?.id, putErr);
                                throw putErr;
                            }
                        }
                    }
                }
            }

            setInfo(`Saved ${written} item(s) to the backend.`);
            await refetchFromBackend();
        } catch (err) {
            console.error("Batch save failed:", err);
            setError(err?.response?.data?.message || "Batch save failed.");
        }
    };

    // Seed (save current items — including samples — to backend)
    const seedBackendFromCurrent = async () => {
        await saveArrayToBackend(items);
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const parsed = parseCsv(text);
            const merged = mergeById(items, parsed);
            setItems(merged);
            // Persist to backend too
            await saveArrayToBackend(merged);
        } catch (err) {
            console.error("Import failed:", err);
            setError("Import failed. Please check the CSV format.");
        } finally {
            e.target.value = "";
        }
    };

    const splitCsvLine = (line) => {
        const result = [];
        let cur = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (inQuotes) {
                if (ch === '"' && line[i + 1] === '"') {
                    cur += '"';
                    i++;
                } else if (ch === '"') {
                    inQuotes = false;
                } else {
                    cur += ch;
                }
            } else {
                if (ch === ",") {
                    result.push(cur);
                    cur = "";
                } else if (ch === '"') {
                    inQuotes = true;
                } else {
                    cur += ch;
                }
            }
        }
        result.push(cur);
        return result;
    };

    const toNumber = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const orEmpty = (v) => (v && v.trim().length ? v : "");

    const slugify = (s) =>
        String(s || "")
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9\-]/g, "")
            .slice(0, 60);

    const parseCsv = (csvText) => {
        const lines = csvText.split(/\r?\n/).filter((l) => l.trim().length);
        if (!lines.length) return [];
        const header = splitCsvLine(lines[0]).map((h) => h.trim());
        const idx = (name) => header.indexOf(name);

        const out = [];
        for (let i = 1; i < lines.length; i++) {
            const cells = splitCsvLine(lines[i]);
            const obj = {
                id: cells[idx("id")] || undefined,
                name: cells[idx("name")] || "",
                description: cells[idx("description")] || "",
                priceUsd: toNumber(cells[idx("priceUsd")]),
                availability: cells[idx("availability")] || "Now",
                availableFrom: orEmpty(cells[idx("availableFrom")]),
                availableTo: orEmpty(cells[idx("availableTo")]),
                customersCount: toNumber(cells[idx("customersCount")]),
                category: cells[idx("category")] || "",
                tags: (cells[idx("tags")] || "")
                    .split(";")
                    .map((s) => s.trim())
                    .filter(Boolean),
                videoUrl: orEmpty(cells[idx("videoUrl")])
            };
            if (!obj.id) {
                obj.id = slugify(obj.name || `tool-${i}-${Date.now()}`);
            }
            out.push(obj);
        }
        return out;
    };

    const mergeById = (base, incoming) => {
        const map = new Map(base.map((b) => [b.id, { ...b }]));
        for (const it of incoming) {
            if (map.has(it.id)) {
                map.set(it.id, { ...map.get(it.id), ...it });
            } else {
                map.set(it.id, it);
            }
        }
        return Array.from(map.values());
    };

    const onDelete = async (item) => {
        // local remove
        setItems((prev) => prev.filter((x) => x.id !== item.id));
        // backend best-effort (DELETE body carries keys)
        if (backEndUrl) {
            try {
                await axios.delete(`${backEndUrl}/consulting/tools`, {
                    headers: { ...authHeaders, "Content-Type": "application/json" },
                    data: { category: item.category, id: item.id }
                });
                setInfo(`Deleted "${item.name}"`);
            } catch (err) {
                console.error("Backend delete failed (kept local removal):", err);
            }
        }
    };

    const refetchFromBackend = async () => {
        if (!backEndUrl) return;
        try {
            setLoading(true);
            const res = await axios.get(`${backEndUrl}/consulting/tools`, {
                headers: authHeaders
            });
            const data = Array.isArray(res.data?.items) ? res.data.items : [];
            if (data.length) setItems(data);
        } catch (err) {
            console.error("Refetch failed:", err);
        } finally {
            setLoading(false);
        }
    };

    // ---- Edit modal helpers ----
    const openEdit = (it) => {
        setEditItem({
            ...it,
            // normalize optional fields for inputs
            availableFrom: it.availableFrom || "",
            availableTo: it.availableTo || "",
            tags: Array.isArray(it.tags) ? it.tags.join(", ") : (it.tags || ""),
            videoUrl: it.videoUrl || ""
        });
        setIsEditOpen(true);
    };

    const closeEdit = () => {
        setIsEditOpen(false);
        setEditItem(null);
    };

    const onEditChange = (field, value) => {
        setEditItem((prev) => ({ ...prev, [field]: value }));
    };

    const saveToolToBackend = async (tool) => {
        if (!backEndUrl) return;
        try {
            // Try create first
            await axios.put(`${backEndUrl}/consulting/tools`, tool, {
                headers: { ...authHeaders, "Content-Type": "application/json" }
            });
        } catch (err) {
            // If already exists, update
            if (err.response?.status === 409) {
                await axios.patch(`${backEndUrl}/consulting/tools`, tool, {
                    headers: { ...authHeaders, "Content-Type": "application/json" }
                });
            } else {
                throw err;
            }
        }
    };

    const onSaveEdit = async () => {
        if (!editItem) return;
        // lock id/category (primary keys).
        const payload = {
            id: editItem.id,
            category: editItem.category,
            name: (editItem.name || "").trim(),
            description: (editItem.description || "").trim(),
            priceUsd: Number(editItem.priceUsd || 0),
            availability: editItem.availability || "Now",
            availableFrom: editItem.availableFrom || "",
            availableTo: editItem.availableTo || "",
            customersCount: Number(editItem.customersCount || 0),
            tags: (editItem.tags || "")
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean),
            videoUrl: (editItem.videoUrl || "").trim()
        };

        if (payload.videoUrl && !isHttpUrl(payload.videoUrl)) {
            setError("Video URL must start with http(s) and be a valid link.");
            return;
        }

        // local optimistic update
        setItems((prev) =>
            prev.map((x) => (x.id === payload.id ? { ...x, ...payload, tags: payload.tags } : x))
        );

        try {
            await saveToolToBackend(payload);
            setInfo(`Saved "${payload.name}"`);
            await refetchFromBackend();
        } catch (err) {
            console.error("Save failed:", err);
            setError(err.response?.data?.message || "Save failed.");
        } finally {
            closeEdit();
        }
    };

    // ---- Add modal helpers ----
    const openAdd = () => {
        setNewItem({
            id: "",
            category: "",
            name: "",
            description: "",
            priceUsd: "",
            availability: "Now",
            availableFrom: "",
            availableTo: "",
            customersCount: "",
            tags: "",
            videoUrl: ""
        });
        setIsAddOpen(true);
    };

    const closeAdd = () => {
        setIsAddOpen(false);
    };

    const onNewChange = (field, value) => {
        setNewItem((prev) => ({ ...prev, [field]: value }));
    };

    const onSaveNew = async () => {
        // derive id if blank
        const id = (newItem.id || slugify(newItem.name)).trim();
        if (!id) {
            setError("Please provide a Name (or an Id).");
            return;
        }
        const payload = {
            id,
            category: (newItem.category || "").trim(),
            name: (newItem.name || "").trim(),
            description: (newItem.description || "").trim(),
            priceUsd: Number(newItem.priceUsd || 0),
            availability: newItem.availability || "Now",
            availableFrom: newItem.availableFrom || "",
            availableTo: newItem.availableTo || "",
            customersCount: Number(newItem.customersCount || 0),
            tags: (newItem.tags || "")
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean),
            videoUrl: (newItem.videoUrl || "").trim()
        };

        if (!payload.category) {
            setError("Category is required.");
            return;
        }
        if (payload.videoUrl && !isHttpUrl(payload.videoUrl)) {
            setError("Video URL must start with http(s) and be a valid link.");
            return;
        }

        // unique id check
        if (items.some((it) => it.id === payload.id)) {
            setError(`An item with id "${payload.id}" already exists.`);
            return;
        }

        // local optimistic insert
        setItems((prev) => [...prev, payload]);

        try {
            await saveToolToBackend(payload);
            setInfo(`Added "${payload.name}"`);
            await refetchFromBackend();
        } catch (err) {
            console.error("Add failed:", err);
            setError(err.response?.data?.message || "Add failed.");
        } finally {
            closeAdd();
        }
    };

    return (
        <div className="bb-container">
            <div
                className="bb-title"
                style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}
            >
                <h2 style={{ margin: 0 }}>Consulting Catalog</h2>
                <span style={{ opacity: 0.8 }}>
          Showing {sorted.length} of {items.length}
        </span>

                {isAdmin && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button onClick={exportCsv}>Export CSV</button>
                        <button onClick={triggerImport}>Import CSV</button>
                        <button onClick={seedBackendFromCurrent} disabled={!backEndUrl}>
                            Save All to Backend
                        </button>
                        <button onClick={openAdd} title="Add a new tool">
                            Add New Tool
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            style={{ display: "none" }}
                            onChange={handleImportFile}
                        />
                    </div>
                )}
            </div>

            {/* Notices */}
            {!!info && (
                <div className="bb-info" style={{ margin: "8px 0", color: "#125" }}>
                    {info}
                </div>
            )}
            {!!error && (
                <div className="bb-error" style={{ margin: "8px 0", color: "#a00" }}>
                    {error}
                </div>
            )}
            {loading && <div className="bb-note">Loading catalog…</div>}

            {/* Filters */}
            <div
                className="bb-filters"
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 180px 180px 140px 140px auto",
                    gap: 10,
                    margin: "12px 0"
                }}
            >
                <input
                    placeholder="Search name, description, category, tags, video URL…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    {categories.map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </select>
                <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
                    {["All", ...availabilityOptions].map((a) => (
                        <option key={a} value={a}>
                            {a}
                        </option>
                    ))}
                </select>
                <input
                    type="number"
                    inputMode="numeric"
                    placeholder="Min price"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                />
                <input
                    type="number"
                    inputMode="numeric"
                    placeholder="Max price"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                />
                <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={clearFilters}>Clear</button>
                </div>
            </div>

            {/* Table */}
            <div className="bb-table-wrapper" style={{ overflowX: "auto" }}>
                <table className="bb-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                    <tr>
                        {headers.map((h) => {
                            const active = h.key === sortKey;
                            const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
                            return (
                                <th
                                    key={h.key}
                                    onClick={() => h.key !== "__actions" && onSort(h.key)}
                                    style={{
                                        cursor: h.key !== "__actions" ? "pointer" : "default",
                                        whiteSpace: "nowrap",
                                        borderBottom: "1px solid #ddd",
                                        padding: "10px 8px",
                                        textAlign: "left"
                                    }}
                                    title={h.key !== "__actions" ? `Sort by ${h.label}` : undefined}
                                >
                                    {h.label} {h.key !== "__actions" ? arrow : ""}
                                </th>
                            );
                        })}
                    </tr>
                    </thead>
                    <tbody>
                    {sorted.map((it) => {
                        const ytId = extractYouTubeId(it.videoUrl || "");
                        return (
                            <tr key={it.id}>
                                <td style={{ padding: "10px 8px", verticalAlign: "top", fontWeight: 600 }}>
                                    {it.name}
                                </td>
                                <td style={{ padding: "10px 8px", verticalAlign: "top", maxWidth: 520 }}>
                                    <div style={{ opacity: 0.9 }}>{it.description}</div>
                                </td>
                                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                                    {currency(it.priceUsd)}
                                </td>
                                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                                    <div>
                                        <b>{it.availability}</b>
                                    </div>
                                    {it.availableFrom && (
                                        <div style={{ fontSize: 12, opacity: 0.8 }}>
                                            From: {dateShort(it.availableFrom)}
                                        </div>
                                    )}
                                    {it.availableTo && (
                                        <div style={{ fontSize: 12, opacity: 0.8 }}>
                                            To: {dateShort(it.availableTo)}
                                        </div>
                                    )}
                                </td>
                                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                                    {Number(it.customersCount ?? 0).toLocaleString()}
                                </td>
                                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                                    {it.category || "—"}
                                </td>
                                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                                    {(it.tags || []).length ? (
                                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                            {it.tags.map((t) => (
                                                <span
                                                    key={t}
                                                    style={{
                                                        fontSize: 12,
                                                        padding: "2px 6px",
                                                        borderRadius: 999,
                                                        border: "1px solid #ddd"
                                                    }}
                                                >
                            {t}
                          </span>
                                            ))}
                                        </div>
                                    ) : (
                                        "—"
                                    )}
                                </td>

                                {/* Demo column */}
                                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                                    {isHttpUrl(it.videoUrl) ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            {!!ytId && (
                                                <img
                                                    alt="thumbnail"
                                                    src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                                                    style={{
                                                        width: 56,
                                                        height: 32,
                                                        objectFit: "cover",
                                                        borderRadius: 4,
                                                        border: "1px solid #ddd"
                                                    }}
                                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                                />
                                            )}
                                            <a href={it.videoUrl} target="_blank" rel="noopener noreferrer">
                                                Watch
                                            </a>
                                        </div>
                                    ) : (
                                        "—"
                                    )}
                                </td>

                                <td
                                    style={{ padding: "10px 8px", verticalAlign: "top", whiteSpace: "nowrap" }}
                                >
                                    {isAdmin ? (
                                        <>
                                            <button style={{ marginRight: 8 }} onClick={() => openEdit(it)}>
                                                Edit
                                            </button>
                                            <button onClick={() => onDelete(it)} title="Delete row">
                                                Delete
                                            </button>
                                        </>
                                    ) : (
                                        <span style={{ opacity: 0.4 }}>—</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {!sorted.length && !loading && (
                        <tr>
                            <td colSpan={headers.length} style={{ padding: 16, textAlign: "center", opacity: 0.8 }}>
                                No matching items.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Footnote */}
            <div className="bb-note" style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                Tip: add <code>?q=ai&amp;sort=-priceUsd</code> to the URL to pre-filter and sort.
            </div>

            {/* Edit Modal */}
            {isAdmin && isEditOpen && editItem && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999
                    }}
                    onClick={closeEdit}
                >
                    <div
                        style={{
                            background: "#fff",
                            padding: 16,
                            borderRadius: 8,
                            minWidth: 520,
                            maxWidth: "90vw"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ marginTop: 0 }}>Edit Tool</h3>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "140px 1fr",
                                gap: 8,
                                alignItems: "center"
                            }}
                        >
                            <label>Id (SK)</label>
                            <input value={editItem.id} disabled />

                            <label>Category (PK)</label>
                            <input value={editItem.category} disabled />

                            <label>Name</label>
                            <input
                                value={editItem.name}
                                onChange={(e) => onEditChange("name", e.target.value)}
                            />

                            <label>Description</label>
                            <textarea
                                rows={4}
                                value={editItem.description}
                                onChange={(e) => onEditChange("description", e.target.value)}
                            />

                            <label>Price (USD)</label>
                            <input
                                type="number"
                                inputMode="numeric"
                                value={editItem.priceUsd}
                                onChange={(e) => onEditChange("priceUsd", e.target.value)}
                            />

                            <label>Availability</label>
                            <select
                                value={editItem.availability}
                                onChange={(e) => onEditChange("availability", e.target.value)}
                            >
                                {availabilityOptions.map((a) => (
                                    <option key={a} value={a}>
                                        {a}
                                    </option>
                                ))}
                            </select>

                            <label>Available From</label>
                            <input
                                type="date"
                                value={editItem.availableFrom}
                                onChange={(e) => onEditChange("availableFrom", e.target.value)}
                            />

                            <label>Available To</label>
                            <input
                                type="date"
                                value={editItem.availableTo}
                                onChange={(e) => onEditChange("availableTo", e.target.value)}
                            />

                            <label>Customers</label>
                            <input
                                type="number"
                                inputMode="numeric"
                                value={editItem.customersCount}
                                onChange={(e) => onEditChange("customersCount", e.target.value)}
                            />

                            <label>Tags (comma-separated)</label>
                            <input
                                value={editItem.tags}
                                onChange={(e) => onEditChange("tags", e.target.value)}
                            />

                            <label>Video URL</label>
                            <input
                                placeholder="https://… (YouTube, Vimeo, etc.)"
                                value={editItem.videoUrl || ""}
                                onChange={(e) => onEditChange("videoUrl", e.target.value)}
                            />
                        </div>

                        <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={closeEdit}>Cancel</button>
                            <button onClick={onSaveEdit}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Modal */}
            {isAdmin && isAddOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999
                    }}
                    onClick={closeAdd}
                >
                    <div
                        style={{
                            background: "#fff",
                            padding: 16,
                            borderRadius: 8,
                            minWidth: 540,
                            maxWidth: "90vw"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ marginTop: 0 }}>Add New Tool</h3>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "180px 1fr",
                                gap: 8,
                                alignItems: "center"
                            }}
                        >
                            <label>Id (SK)</label>
                            <input
                                placeholder="auto from name if left blank"
                                value={newItem.id}
                                onChange={(e) => onNewChange("id", e.target.value)}
                            />

                            <label>Category (PK)</label>
                            <input
                                placeholder="e.g., LMS, Integrations, AI, Content..."
                                value={newItem.category}
                                onChange={(e) => onNewChange("category", e.target.value)}
                            />

                            <label>Name</label>
                            <input
                                value={newItem.name}
                                onChange={(e) => onNewChange("name", e.target.value)}
                            />

                            <label>Description</label>
                            <textarea
                                rows={4}
                                value={newItem.description}
                                onChange={(e) => onNewChange("description", e.target.value)}
                            />

                            <label>Price (USD)</label>
                            <input
                                type="number"
                                inputMode="numeric"
                                value={newItem.priceUsd}
                                onChange={(e) => onNewChange("priceUsd", e.target.value)}
                            />

                            <label>Availability</label>
                            <select
                                value={newItem.availability}
                                onChange={(e) => onNewChange("availability", e.target.value)}
                            >
                                {availabilityOptions.map((a) => (
                                    <option key={a} value={a}>
                                        {a}
                                    </option>
                                ))}
                            </select>

                            <label>Available From</label>
                            <input
                                type="date"
                                value={newItem.availableFrom}
                                onChange={(e) => onNewChange("availableFrom", e.target.value)}
                            />

                            <label>Available To</label>
                            <input
                                type="date"
                                value={newItem.availableTo}
                                onChange={(e) => onNewChange("availableTo", e.target.value)}
                            />

                            <label>Customers</label>
                            <input
                                type="number"
                                inputMode="numeric"
                                value={newItem.customersCount}
                                onChange={(e) => onNewChange("customersCount", e.target.value)}
                            />

                            <label>Tags (comma-separated)</label>
                            <input
                                placeholder="e.g., LTI 1.3, Gradebook, IMS"
                                value={newItem.tags}
                                onChange={(e) => onNewChange("tags", e.target.value)}
                            />

                            <label>Video URL</label>
                            <input
                                placeholder="https://… (YouTube, Vimeo, etc.)"
                                value={newItem.videoUrl}
                                onChange={(e) => onNewChange("videoUrl", e.target.value)}
                            />
                        </div>

                        <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={closeAdd}>Cancel</button>
                            <button onClick={onSaveNew}>Add</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsultingCatalog;
